/**
 * The option form shared by Convert Image, Bulk Image and WebP Converter: its state shape, how it becomes the
 * option object the converter (and the API) understand, and the few small rules that depend on it.
 * No React and no canvas in here so the rules can be tested.
 */
import { formatInfo, type FitMode, type OutFormat } from "./convertCore";

export interface ConvertForm {
  format: OutFormat;
  /** Fill colour as #rrggbb. */
  bgColor: string;
  /** Fill transparent areas and padding even when the format could keep them. */
  useBg: boolean;
  quality: number;
  width: string;
  height: string;
  fit: FitMode;
  noEnlarge: boolean;
  rotate: string;
  flip: boolean;
  flop: boolean;
  grayscale: boolean;
  autoOrient: boolean;
  keepMeta: boolean;
  dpi: string;
  icoSizes: number[];
  bmpDepth: string;
}

export const DEFAULT_FORM: ConvertForm = {
  format: "png", bgColor: "#ffffff", useBg: false, quality: 92, width: "", height: "", fit: "inside", noEnlarge: false,
  rotate: "0", flip: false, flop: false, grayscale: false, autoOrient: true, keepMeta: false, dpi: "", icoSizes: [16, 32, 48], bmpDepth: "24",
};

export const FIT_HELP: Record<FitMode, string> = {
  inside: "Fits inside the size you give and keeps the picture's shape. Nothing is cropped or padded.",
  contain: "Fits inside the exact size and fills the leftover space with the background colour.",
  cover: "Fills the exact size and crops whatever sticks out.",
  fill: "Stretches the picture to the exact size. It can look squashed.",
  outside: "Keeps the shape and makes the picture at least as big as the size you give.",
};

/** JPEG and 24-bit BMP cannot be see-through, so they always need a fill colour. */
export function needsFill(f: Pick<ConvertForm, "format" | "bmpDepth">): boolean {
  return f.format === "jpeg" || (f.format === "bmp" && f.bmpDepth === "24");
}

/** Camera and location details can be carried over only from a JPEG into a JPEG. */
export function metaCanBeKept(format: OutFormat, sourceType: string): boolean {
  return format === "jpeg" && sourceType === "image/jpeg";
}

/** The raw option record that parseConvertOptions validates. */
export function formToRaw(f: ConvertForm): Record<string, unknown> {
  return {
    format: f.format,
    quality: formatInfo(f.format).lossy ? f.quality : undefined,
    backgroundColor: needsFill(f) || f.useBg ? f.bgColor : undefined,
    width: f.width, height: f.height, fit: f.fit, withoutEnlargement: f.noEnlarge,
    rotate: f.rotate, flip: f.flip, flop: f.flop, grayscale: f.grayscale, autoOrient: f.autoOrient, stripMetadata: !f.keepMeta, dpi: f.dpi,
    sizes: f.icoSizes.join(","), bitDepth: f.bmpDepth,
  };
}

/**
 * Bulk Image's "Longest side" shortcut: with no width or height set, every picture is shrunk so its longest side is
 * at most that many pixels (never enlarged). A width or height typed in the size boxes takes over.
 */
export function applyLongestSide(raw: Record<string, unknown>, longest: string): Record<string, unknown> {
  const n = Math.round(Number(longest));
  const blank = (v: unknown) => v === undefined || v === null || String(v).trim() === "";
  if (!(n > 0) || !blank(raw.width) || !blank(raw.height)) return raw;
  return { ...raw, width: n, height: n, fit: "inside", withoutEnlargement: true };
}

/** Rewrites the parser's "'width' ..." messages with the names the page shows. */
export function friendlyOptionError(message: string): string {
  const names: Record<string, string> = { width: "Width", height: "Height", dpi: "Resolution", sizes: "Icon sizes" };
  return message.replace(/^'(\w+)'/, (_m, k: string) => names[k] ?? `'${k}'`);
}

/** The first format a converted file should default to for a given source (never "same format"). */
export function defaultTargetFor(type: string, kind: string): OutFormat {
  if (kind === "heic" || kind === "tiff") return "jpeg";
  if (type === "image/png") return "jpeg";
  if (type === "image/jpeg") return "png";
  if (type === "image/webp") return "jpeg";
  return "png";
}

/** "a.png" -> "a-2.png" when "a.png" is already taken (case-insensitive); records the name it returns. */
export function uniqueName(wanted: string, used: Set<string>): string {
  const dot = wanted.lastIndexOf(".");
  const base = dot > 0 ? wanted.slice(0, dot) : wanted;
  const ext = dot > 0 ? wanted.slice(dot) : "";
  let name = wanted, n = 1;
  while (used.has(name.toLowerCase())) name = `${base}-${++n}${ext}`;
  used.add(name.toLowerCase());
  return name;
}

/** Name for a converted file inside the batch ZIP: the original base name with the new extension, never repeated. */
export function bulkOutputName(fileName: string, format: OutFormat, used: Set<string>): string {
  const base = fileName.replace(/\.[^./\\]+$/, "") || "image";
  return uniqueName(`${base}.${formatInfo(format).ext}`, used);
}

/** One result line for the batch list. */
export function bulkRowDetail(origBytes: number, outBytes: number, dims: string): string {
  const grew = outBytes > origBytes;
  return `${(origBytes / 1024).toFixed(0)} KB -> ${(outBytes / 1024).toFixed(0)} KB, ${dims}${grew ? " (larger than the original)" : ""}`;
}

/** Pixel size of a picture after it is turned: a quarter turn swaps width and height. */
export function turnedSize(w: number, h: number, rotate: string | number): { w: number; h: number } {
  const r = Number(rotate);
  return r === 90 || r === 270 ? { w: h, h: w } : { w, h };
}
